import { ethers } from 'ethers';
import {
  depositRequest,
  depositSuccess,
  depositFail,
  withdrawRequest,
  withdrawSuccess,
  withdrawFail,
  swapRequest,
  swapSuccess,
  swapFail,
  swapsLoaded,
} from './reducers/amm';
import { setAccount, setNetwork, setError } from './reducers/provider';
import { tokenBalancesLoaded, setSymbols } from './reducers/tokens';
import { getNetworkConfig } from '../utils/network';
import AMM_ABI from '../abis/AMM.json';
import TOKEN_ABI from '../abis/Token.json';

let accountListenerAttached = false;
let chainListenerAttached = false;
let accountHandler = null;
let chainHandler = null;

/**
 * Quote via Aggregator.getPriceData, execute via selected AMM.swap.
 * Aggregator.performSwap cannot pull tokens from the user (msg.sender = Aggregator),
 * so execution is: best AMM from getPriceData → approve that AMM → AMM.swap.
 */
export const swap = async (
  provider,
  {
    aggregatorContract,
    amm1Contract,
    amm2Contract,
    tokenIn,
    tokenOut,
    amountIn,
  },
  dispatch
) => {
  try {
    dispatch(swapRequest());

    if (!provider || !aggregatorContract || !amm1Contract || !amm2Contract) {
      throw new Error('Missing provider or contracts');
    }
    if (!tokenIn || !tokenOut || !amountIn) {
      throw new Error('Missing swap parameters');
    }

    const signer = provider.getSigner();
    const [, bestAMM] = await aggregatorContract.getPriceData(
      tokenIn.address,
      tokenOut.address
    );

    let selectedAmm = null;
    if (bestAMM === 'AMM1') selectedAmm = amm1Contract;
    else if (bestAMM === 'AMM2') selectedAmm = amm2Contract;
    else throw new Error(`Unknown best AMM: ${bestAMM}`);

    await (await tokenIn.connect(signer).approve(selectedAmm.address, amountIn)).wait();

    const transaction = await selectedAmm
      .connect(signer)
      .swap(tokenIn.address, tokenOut.address, amountIn);
    await transaction.wait();

    dispatch(swapSuccess(transaction.hash));
    return { success: true, bestAMM, transactionHash: transaction.hash };
  } catch (error) {
    console.error('Error swapping tokens:', error.message || error);
    dispatch(swapFail());
    dispatch(setError(error.message || 'Failed to swap tokens.'));
    throw error;
  }
};

export const addLiquidity = async (
  provider,
  amm,
  tokenA,
  tokenB,
  amountA,
  amountB,
  dispatch
) => {
  try {
    dispatch(depositRequest());
    const signer = provider.getSigner();

    await Promise.all([
      (await tokenA.connect(signer).approve(amm.address, amountA)).wait(),
      (await tokenB.connect(signer).approve(amm.address, amountB)).wait(),
    ]);

    const transaction = await amm
      .connect(signer)
      .addLiquidity(tokenA.address, tokenB.address, amountA, amountB);
    await transaction.wait();

    dispatch(depositSuccess(transaction.hash));
    return { success: true, transactionHash: transaction.hash };
  } catch (error) {
    console.error('Error adding liquidity:', error.message || error);
    dispatch(depositFail());
    dispatch(setError(error.message || 'Failed to add liquidity.'));
    throw error;
  }
};

export const removeLiquidity = async (
  provider,
  amm,
  tokenA,
  tokenB,
  amountA,
  amountB,
  dispatch
) => {
  try {
    dispatch(withdrawRequest());
    const signer = provider.getSigner();
    const transaction = await amm
      .connect(signer)
      .removeLiquidity(tokenA.address, tokenB.address, amountA, amountB);
    await transaction.wait();
    dispatch(withdrawSuccess(transaction.hash));
    return { success: true, transactionHash: transaction.hash };
  } catch (error) {
    console.error('Error removing liquidity:', error.message || error);
    dispatch(withdrawFail());
    dispatch(setError(error.message || 'Failed to remove liquidity.'));
    throw error;
  }
};

export const loadBalances = async (provider, networkConfig, dispatch) => {
  try {
    if (!provider || !networkConfig) return;

    const amm1Contract = new ethers.Contract(
      networkConfig.amm1.ammAddress,
      AMM_ABI,
      provider
    );
    const amm2Contract = new ethers.Contract(
      networkConfig.amm2.ammAddress,
      AMM_ABI,
      provider
    );

    const [
      amm1DappTokenBalance,
      amm1UsdTokenBalance,
      amm2DappTokenBalance,
      amm2UsdTokenBalance,
    ] = await Promise.all([
      amm1Contract.getTokenBalance(networkConfig.amm1.dappTokenAddress),
      amm1Contract.getTokenBalance(networkConfig.amm1.usdTokenAddress),
      amm2Contract.getTokenBalance(networkConfig.amm2.dappTokenAddress),
      amm2Contract.getTokenBalance(networkConfig.amm2.usdTokenAddress),
    ]);

    dispatch(
      tokenBalancesLoaded({
        amm1: {
          dappTokenBalance: ethers.utils.formatEther(amm1DappTokenBalance),
          usdTokenBalance: ethers.utils.formatEther(amm1UsdTokenBalance),
        },
        amm2: {
          dappTokenBalance: ethers.utils.formatEther(amm2DappTokenBalance),
          usdTokenBalance: ethers.utils.formatEther(amm2UsdTokenBalance),
        },
      })
    );
  } catch (error) {
    console.error('Error loading AMM balances:', error.message || error);
    dispatch(setError('Failed to load AMM balances.'));
  }
};

export const loadTokenSymbols = async (provider, networkConfig, dispatch) => {
  try {
    if (!provider || !networkConfig) return;
    const dapp = new ethers.Contract(
      networkConfig.amm1.dappTokenAddress,
      TOKEN_ABI,
      provider
    );
    const usd = new ethers.Contract(
      networkConfig.amm1.usdTokenAddress,
      TOKEN_ABI,
      provider
    );
    const [dappSymbol, usdSymbol] = await Promise.all([dapp.symbol(), usd.symbol()]);
    dispatch(setSymbols([dappSymbol, usdSymbol, dappSymbol, usdSymbol]));
  } catch (error) {
    console.error('Error loading token symbols:', error.message || error);
  }
};

export const loadAllSwaps = async (provider, amm, dispatch) => {
  if (!provider || !amm) return;
  try {
    const block = await provider.getBlockNumber();
    const swapStream = await amm.queryFilter('TokensSwapped', 0, block);
    const swaps = swapStream.map((event) => ({
      hash: event.transactionHash,
      args: event.args,
    }));
    dispatch(swapsLoaded(swaps));
  } catch (error) {
    console.error('Error loading swaps:', error.message || error);
  }
};

export const watchAccountChanges = (dispatch) => {
  if (!window.ethereum || accountListenerAttached) return () => {};

  accountHandler = (accounts) => {
    if (accounts && accounts.length > 0) {
      dispatch(setAccount(ethers.utils.getAddress(accounts[0])));
    } else {
      dispatch(setAccount(''));
    }
  };

  window.ethereum.on('accountsChanged', accountHandler);
  accountListenerAttached = true;

  return () => {
    if (window.ethereum && accountHandler) {
      window.ethereum.removeListener('accountsChanged', accountHandler);
    }
    accountListenerAttached = false;
    accountHandler = null;
  };
};

export const watchNetworkChanges = (dispatch, onChainChanged) => {
  if (!window.ethereum || chainListenerAttached) return () => {};

  chainHandler = async (chainIdHex) => {
    const chainId = parseInt(chainIdHex, 16);
    dispatch(setNetwork(chainId));
    if (typeof onChainChanged === 'function') {
      await onChainChanged(chainId);
    }
  };

  window.ethereum.on('chainChanged', chainHandler);
  chainListenerAttached = true;

  return () => {
    if (window.ethereum && chainHandler) {
      window.ethereum.removeListener('chainChanged', chainHandler);
    }
    chainListenerAttached = false;
    chainHandler = null;
  };
};

export const applyNetworkConfig = (chainId, dispatch) => {
  const networkConfig = getNetworkConfig(chainId);
  return networkConfig;
};
